import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Container,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { forgotPassword } from '../../utils/api';
import { useLanguage } from '../../utils/LanguageContext';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const content = {
    en: {
      title: 'Forgot Password',
      subtitle: 'Enter your email and we\'ll send you a reset link',
      emailLabel: 'Email Address',
      sendBtn: 'Send Reset Link',
      backToLogin: 'Back to Sign In',
      sentTitle: 'Check Your Email',
      sentMessage: 'If an account exists with this email, you\'ll receive a password reset link shortly.',
      sentNote: 'The link will expire in 1 hour.',
      tryAgain: 'Try another email',
      validEmail: 'Please enter a valid email',
    },
    my: {
      title: 'Lupa Kata Laluan',
      subtitle: 'Masukkan emel anda dan kami akan hantar pautan tetapan semula',
      emailLabel: 'Alamat Emel',
      sendBtn: 'Hantar Pautan',
      backToLogin: 'Kembali ke Log Masuk',
      sentTitle: 'Semak Emel Anda',
      sentMessage: 'Jika akaun wujud dengan emel ini, anda akan menerima pautan tetapan semula kata laluan.',
      sentNote: 'Pautan akan tamat dalam 1 jam.',
      tryAgain: 'Cuba emel lain',
      validEmail: 'Sila masukkan emel yang sah',
    }
  };

  const t = content[language] || content.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError(t.validEmail);
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset link');
    } finally {
      setLoading(false);
    }
  };

  // Success screen after email sent
  if (sent) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'success.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          </Box>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            {t.sentTitle}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t.sentMessage}
          </Typography>

          <Typography variant="body1" fontWeight={600} color="primary" sx={{ mb: 3 }}>
            {email}
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            {t.sentNote}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setSent(false);
                setEmail('');
              }}
            >
              {t.tryAgain}
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/auth')}
            >
              {t.backToLogin}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/auth')}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          {t.backToLogin}
        </Button>

        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t.subtitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label={t.emailLabel}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            margin="normal"
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : t.sendBtn}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword;
